import { useState, useCallback, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin } from 'lucide-react';

import { MasterPageHeader }   from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { MasterFlash }         from '@/components/master/MasterFlash';
import { MasterAuditTab }      from '@/components/master/MasterAuditTab';
import { MasterConfirmDialogs } from '@/components/master/MasterConfirmDialogs';
import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import {
  Badge, Card, Input, Tabs, Textarea, Toggle,
} from '@/components/ui';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useFlashMessage }     from '@/hooks/useFlashMessage';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { useMasterRecord, masterListFetcher } from '@/hooks/useMasterRecord';
import { Permissions }         from '@/constants/permissions';
import { locationApi }         from '@/api/location.api';
import type { LocationDto, LocationListDto } from '@/types/location.types';
import { formatDateTime } from '@/utils/format';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  code:         z.string().min(1).max(20),
  name:         z.string().min(1, 'Name is required').max(200),
  shortName:    z.string().max(50).nullable().optional(),
  addressLine1: z.string().max(200).nullable().optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  city:         z.string().max(100).nullable().optional(),
  district:     z.string().max(100).nullable().optional(),
  province:     z.string().max(100).nullable().optional(),
  postalCode:   z.string().max(20).nullable().optional(),
  country:      z.string().max(100).nullable().optional(),
  phone:        z.string().max(50).nullable().optional(),
  mobile:       z.string().max(50).nullable().optional(),
  email:        z.string().email().nullable().optional().or(z.literal('')),
  isActive:     z.boolean(),
  isDefault:    z.boolean(),
  remarks:      z.string().max(500).nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  code: '', name: '', shortName: null, addressLine1: null, addressLine2: null,
  city: null, district: null, province: null, postalCode: null, country: null,
  phone: null, mobile: null, email: null, isActive: true, isDefault: false, remarks: null,
};

const TABS = ['General', 'Address & Contact', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const SEARCH_COLUMNS: SearchColumn<LocationListDto>[] = [
  { key: 'code',      label: 'Code',     className: 'w-20 font-mono' },
  { key: 'name',      label: 'Name' },
  { key: 'shortName', label: 'Short',    className: 'w-24', render: r => r.shortName ?? '—' },
  { key: 'city',      label: 'City',     className: 'w-24', render: r => r.city ?? '—' },
  { key: 'country',   label: 'Country',  className: 'w-24', render: r => r.country ?? '—' },
  { key: 'isDefault', label: 'Default',  className: 'w-20',
    render: r => r.isDefault ? <Badge variant="success">Default</Badge> : null },
  { key: 'isActive',  label: 'Status',   className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

function dtoToForm(dto: LocationDto): FormValues {
  return {
    code: dto.code, name: dto.name, shortName: dto.shortName ?? null,
    addressLine1: dto.addressLine1 ?? null, addressLine2: dto.addressLine2 ?? null,
    city: dto.city ?? null, district: dto.district ?? null,
    province: dto.province ?? null, postalCode: dto.postalCode ?? null,
    country: dto.country ?? null, phone: dto.phone ?? null,
    mobile: dto.mobile ?? null, email: dto.email ?? null,
    isActive: dto.isActive, isDefault: dto.isDefault, remarks: dto.remarks ?? null,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const LocationPage = () => {
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.MST_LOCATION_VIEW);
  const canCreate = hasPermission(Permissions.MST_LOCATION_CREATE);
  const canEdit   = hasPermission(Permissions.MST_LOCATION_EDIT);
  const canDelete = hasPermission(Permissions.MST_LOCATION_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });

  const { flashMsg, flash } = useFlashMessage();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const [codeInput,    setCodeInput]    = useState('');

  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });

  const master = useMasterRecord<LocationDto, Tab, FormValues>({
    screen, flash, firstTab: 'General', focusRef: codeInputRef,
    clearForm:   () => reset(EMPTY_FORM),
    clearScreen: () => { setCodeInput(''); },
    onDelete: {
      remove:   id => locationApi.remove(id),
      describe: r => `Location '${r.code}'`,
    },
  });

  const {
    record, setRecord, activeTab, setActiveTab, saving, setSaving, lookupBusy, setLookupBusy,
    printOpen, setPrintOpen, searchOpen, setSearchOpen,
    pendingSave, setPendingSave, showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm, resetToInitial, executeDelete, beginSave,
  } = master;


  const loadRecord = useCallback((dto: LocationDto) => {
    setRecord(dto); reset(dtoToForm(dto)); setCodeInput(dto.code);
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleCodeLookup = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    if (!canView) { flash('error', 'You do not have permission to view locations.'); return; }
    setLookupBusy(true);
    try {
      const res = await locationApi.getByCode(code);
      if (res.data.data) loadRecord(res.data.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        if (!canCreate) { flash('error', `Location '${code}' was not found.`); return; }
        setShowNewConfirm(code);
      } else { flash('error', 'Lookup failed. Please try again.'); }
    } finally { setLookupBusy(false); }
  };

  const handleConfirmCreateNew = () => {
    const code = showNewConfirm; setShowNewConfirm(null); if (!code) return;
    reset({ ...EMPTY_FORM, code }); setCodeInput(code);
    setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleSaveClick = () => {
    // The second argument runs when the form is invalid. Without it a failed validation did
    // nothing at all -- no save, no message, no focus -- so the button looked broken rather than
    // the form looking wrong.
    handleSubmit(beginSave, (formErrors) => {
      const first = Object.values(formErrors)[0]?.message;
      flash('error', first ? String(first) : 'Please correct the highlighted fields.');
    })();
  };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    const n = (v: string | null | undefined) => v?.trim() || null;
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await locationApi.create({
          code: d.code.trim().toUpperCase(), name: d.name.trim(),
          shortName: n(d.shortName), addressLine1: n(d.addressLine1), addressLine2: n(d.addressLine2),
          city: n(d.city), district: n(d.district), province: n(d.province),
          postalCode: n(d.postalCode), country: n(d.country),
          phone: n(d.phone), mobile: n(d.mobile), email: n(d.email),
        });
        flash('success', 'Location created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await locationApi.update(record.id, {
          name: d.name.trim(), shortName: n(d.shortName),
          addressLine1: n(d.addressLine1), addressLine2: n(d.addressLine2),
          city: n(d.city), district: n(d.district), province: n(d.province),
          postalCode: n(d.postalCode), country: n(d.country),
          phone: n(d.phone), mobile: n(d.mobile), email: n(d.email),
          isActive: d.isActive, remarks: n(d.remarks),
        });
        flash('success', 'Location updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const body = (err as { response?: { data?: { message?: string;
                       errors?: { message?: string }[] } } }).response?.data;
      // errors[0] names the field. The envelope's own message is just "Validation failed."
      const msg = body?.errors?.[0]?.message ?? body?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };


  const fetchLocations = useMemo(() => masterListFetcher(locationApi.getList), []);

  const handleSearchSelect = (item: LocationListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    locationApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load location.'))
      .finally(() => setLookupBusy(false));
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  const ToggleField = ({ name, label, description }: { name: 'isActive' | 'isDefault'; label: string; description?: string }) => (
    <Controller name={name} control={control} render={({ field }) => (
      <Toggle
        label={label}
        description={description}
        checked={field.value as boolean}
        onChange={field.onChange}
        disabled={fieldsDisabled}
      />
    )} />
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader icon={MapPin} title="Location Maintenance"
        subtitle="Manage branches, outlets and office locations" screen={screen} />

      <MasterToolbar
        {...screen.toolbar} {...master.toolbar}
        saving={saving} loading={lookupBusy} onSave={handleSaveClick}
      />

      <MasterFlash message={flashMsg} />

      <MasterLookupBar
        label="Location Code"
        summary={record && (<>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.name}</span>
          {record.isDefault && <Badge variant="success">Default</Badge>}
          {!record.isActive && (
            <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
          )}
        </>)}
        hint={screen.isInitial ? "Enter a location code and press Enter, or browse." : undefined}
      >
        <MasterCodeLookup
          inputRef={codeInputRef}
          value={codeInput}
          onChange={setCodeInput}
          onSubmit={handleCodeLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search locations"
          placeholder="Type code + Enter…"
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Location Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Location Code" {...register('code')} disabled
                    placeholder="— enter via code field above —" className="font-mono uppercase" />
                  <Input label="Location Name *" {...register('name')} error={errors.name?.message}
                    disabled={fieldsDisabled} placeholder="e.g. Head Office" />
                  <Input label="Short Name" {...register('shortName')} disabled={fieldsDisabled}
                    placeholder="e.g. HO" />
                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2 space-y-2">
                      <ToggleField name="isActive" label="Is Active" description="Inactive locations are hidden from transaction entry." />
                      <ToggleField name="isDefault" label="Is Default Location" description="Default location is pre-selected for new transactions." />
                    </div>
                  )}
                  <Textarea label="Remarks" {...register('remarks')} disabled={fieldsDisabled}
                    rows={3} maxLength={500} placeholder="Optional notes…"
                    className="resize-none" fieldClassName="sm:col-span-2" />
                </div>
              </Card>
            )}

            {activeTab === 'Address & Contact' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Address</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Input label="Address Line 1" {...register('addressLine1')} disabled={fieldsDisabled}
                      maxLength={200} placeholder="Street / Unit number" />
                  </div>
                  <div className="sm:col-span-2">
                    <Input label="Address Line 2" {...register('addressLine2')} disabled={fieldsDisabled}
                      maxLength={200} placeholder="Area / Building" />
                  </div>
                  {[
                    { name: 'city' as const,       label: 'City',        ph: 'e.g. Colombo' },
                    { name: 'district' as const,   label: 'District',    ph: '' },
                    { name: 'province' as const,   label: 'Province',    ph: '' },
                    { name: 'postalCode' as const, label: 'Postal Code', ph: '' },
                    { name: 'country' as const,    label: 'Country',     ph: 'e.g. Sri Lanka' },
                  ].map(({ name, label, ph }) => (
                    <Input key={name} label={label} {...register(name)}
                      disabled={fieldsDisabled} placeholder={ph} />
                  ))}
                </div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4 mt-6">Contact</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { name: 'phone' as const,  label: 'Phone',  ph: '' },
                    { name: 'mobile' as const, label: 'Mobile', ph: '' },
                    { name: 'email' as const,  label: 'Email',  ph: 'e.g. office@example.com' },
                  ].map(({ name, label, ph }) => (
                    <Input key={name} label={label} {...register(name)}
                      disabled={fieldsDisabled} placeholder={ph}
                      type={name === 'email' ? 'email' : 'text'}
                      error={(errors[name] as { message?: string })?.message} />
                  ))}
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && <MasterAuditTab record={record} />}
          </div>
        </form>
      </div>

      {/* Modals */}
      <MasterSearchModal<LocationListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Locations" columns={SEARCH_COLUMNS}
        fetchData={fetchLocations} onSelect={handleSearchSelect}
      />

      <MasterConfirmDialogs
        entity="Location"
        screen={screen}
        record={record}
        describe={r => `${r.code} — ${r.name}`}
        saving={saving}
        create={{
          label: showNewConfirm,
          onConfirm: handleConfirmCreateNew,
          onCancel: () => setShowNewConfirm(null),
        }}
        saveOpen={showSaveConfirm}
        onSaveConfirm={executeSave}
        onSaveCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
        deleteOpen={showDeleteConfirm}
        onDeleteConfirm={executeDelete}
        onDeleteCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Location Report">
          <ReportHeader reportTitle="Location Master Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[{ label: 'Location Code', value: record.code }, { label: 'Status', value: record.isActive ? 'Active' : 'Inactive' }]}
          />
          <ReportSection title="Location Details" fields={[
            { label: 'Code',     value: record.code },
            { label: 'Name',     value: record.name, wide: true },
            { label: 'Short Name', value: record.shortName },
            { label: 'Default',  value: record.isDefault, display: record.isDefault ? 'Yes' : 'No' },
            { label: 'Status',   value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
          ]} />
          <ReportSection title="Address & Contact" fields={[
            { label: 'Address Line 1', value: record.addressLine1, wide: true },
            { label: 'Address Line 2', value: record.addressLine2, wide: true },
            { label: 'City',           value: record.city },
            { label: 'District',       value: record.district },
            { label: 'Province',       value: record.province },
            { label: 'Postal Code',    value: record.postalCode },
            { label: 'Country',        value: record.country },
            { label: 'Phone',          value: record.phone },
            { label: 'Mobile',         value: record.mobile },
            { label: 'Email',          value: record.email },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: formatDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: formatDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
